// Check-in alarms in the browser (architecture sprint-14 §8.2). Mounted once in AppShell beside CheckinController.
// The server owns the slots; this rings on the server-projected clock, loops until "I'm awake" (past the deadline
// too), pre-schedules the next mark on the audio clock when it is within 10 min, records when each mark rang,
// confirms with retries, syncs other tabs, and stops as soon as `alarm` turns null (End, auto-close, void,
// alarms off). It also owns the shell-wide arm / blocked / retrying banners and the heartbeat.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError } from '@/lib/api';
import { useMe } from '@/hooks/use-me';
import { postConfirm, setAlarmSnapshot, useAlarm } from '@/hooks/use-alarm';
import { useAlarmHeartbeat } from '@/hooks/use-alarm-heartbeat';
import { arm, cancelScheduled, isArmed, isLooping, MAX_SCHEDULE_DELAY_SEC, onArmedChange, playTest, scheduleLoop, scheduledStartedSecondsAgo, startLoop, stopLoop } from '@/lib/alarm-audio';
import { alarmServerNowMs, confirmRetryDelayMs, rangPayload, recordRang, ringingKeys, secondsUntil, type AlarmAck } from '@/lib/alarm-model';
import { readAck, readRang, storeAck, storeRang } from '@/lib/alarm-store';
import { setSoundBlocked, useSoundBlocked } from '@/lib/alarm-ui-state';
import { showAlarmNotification } from '@/lib/notifications';
import { clockIn } from '@/lib/shift-format';
import { broadcast, subscribe } from '@/lib/sync';
import { useNowTick } from '@/lib/use-now';
import { AlarmOverlay } from './AlarmOverlay';

const TICK_MS = 1000;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60_000;
const HTTP_SERVER_ERROR_MIN = 500;
export const ARM_BANNER = 'Click to arm alarms';
export const BLOCKED_BANNER = 'Sound is blocked. Click to enable alarms.';
export const RETRY_BANNER = 'Check-in not recorded yet. Retrying…';

/** Network failures and 5xx are retried; any other answer is final (the server is the arbiter). */
function retryable(err: unknown): boolean {
  return !(err instanceof ApiClientError) || err.statusCode >= HTTP_SERVER_ERROR_MIN;
}

export function AlarmController(): JSX.Element | null {
  const snap = useAlarm();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const perfNow = useNowTick(TICK_MS);
  const [ack, setAck] = useState<AlarmAck | null>(() => readAck());
  const [armed, setArmed] = useState(() => isArmed());
  const [retrying, setRetrying] = useState(false);
  const blocked = useSoundBlocked();
  const notified = useRef(new Set<string>());
  const retryTimer = useRef<number | null>(null);
  const shiftRef = useRef<string | null>(null);

  const alarm = snap?.alarm ?? null;
  shiftRef.current = alarm?.shiftId ?? null;
  const tz = me?.timezone ?? 'UTC';
  const serverNow = alarm && snap ? alarmServerNowMs({ alarm, receivedAt: snap.receivedAt }, perfNow) : 0;
  const ringing = alarm ? ringingKeys(alarm, serverNow, ack) : [];
  const ringingSig = ringing.join(',');

  useAlarmHeartbeat(alarm !== null);
  useEffect(() => onArmedChange(setArmed), []);

  // Ring, pre-schedule or stay quiet. Re-evaluated every tick on the projected clock.
  useEffect(() => {
    if (!alarm) {
      stopLoop();
      return;
    }
    if (ringing.length === 0) {
      if (isLooping()) stopLoop();
      const sec = secondsUntil(alarm.nextMarkAt, serverNow);
      if (armed && sec !== null && sec >= 0 && sec <= MAX_SCHEDULE_DELAY_SEC) scheduleLoop(alarm.sound, sec);
      else cancelScheduled();
      return;
    }
    // A pre-scheduled source already sounding started exactly at the mark: report that as the ring start.
    const ago = scheduledStartedSecondsAgo();
    startLoop(alarm.sound);
    const rangAt = new Date(ago !== null ? serverNow - ago * MS_PER_SECOND : serverNow).toISOString();
    let rang = readRang(alarm.shiftId);
    for (const key of ringing) rang = recordRang(rang, key, Date.parse(rangAt) < Date.parse(key) ? key : rangAt);
    storeRang(alarm.shiftId, rang);
    const newest = ringing[ringing.length - 1];
    if (newest && !notified.current.has(newest)) {
      notified.current.add(newest);
      showAlarmNotification(`alarm:${alarm.shiftId}:${newest}`, clockIn(deadlineOf(newest), tz));
    }
    // serverNow advances every tick; the signature keeps the dependency list honest without re-running on identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarm, ringingSig, armed, perfNow]);

  // Any click or key while not armed arms the audio (a one-shot listener; it must run inside the gesture).
  useEffect(() => {
    if (!alarm || armed) return undefined;
    const unlock = (): void => {
      void arm();
    };
    document.addEventListener('pointerdown', unlock, { once: true, capture: true });
    document.addEventListener('keydown', unlock, { once: true, capture: true });
    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true });
      document.removeEventListener('keydown', unlock, { capture: true });
    };
  }, [alarm, armed]);

  const send = useCallback(
    (shiftId: string, upTo: string, attempt: number): void => {
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      const keys = Object.keys(readRang(shiftId)).filter((k) => Date.parse(k) <= Date.parse(upTo));
      postConfirm(rangPayload(readRang(shiftId), keys))
        .then((res) => {
          setAck(storeAck({ shiftId, upTo, sent: true }));
          setAlarmSnapshot(queryClient, res.alarm);
          setRetrying(false);
        })
        .catch((err: unknown) => {
          // Stop retrying when the shift is gone (End, auto-close) or the server gave a final answer.
          if (!retryable(err) || shiftRef.current !== shiftId) {
            setRetrying(false);
            return;
          }
          setRetrying(true);
          retryTimer.current = window.setTimeout(() => send(shiftId, upTo, attempt + 1), confirmRetryDelayMs(attempt));
        });
    },
    [queryClient],
  );

  // On mount (and when the shift changes): an ack this browser never got through is sent again.
  useEffect(() => {
    if (!alarm) return;
    const stored = readAck();
    if (stored && stored.shiftId === alarm.shiftId && !stored.sent) send(stored.shiftId, stored.upTo, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarm?.shiftId]);

  useEffect(
    () => () => {
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    },
    [],
  );

  // Another tab pressed "I'm awake": stop here too.
  useEffect(
    () =>
      subscribe((msg) => {
        if (msg.type !== 'alarm-confirmed') return;
        stopLoop();
        setAck((current) => {
          const stored = readAck();
          const incoming = { shiftId: msg.shiftId, upTo: msg.upTo, sent: true };
          if (stored && stored.shiftId === msg.shiftId && Date.parse(stored.upTo) >= Date.parse(msg.upTo)) return stored;
          return current && current.shiftId === msg.shiftId && Date.parse(current.upTo) >= Date.parse(msg.upTo) ? current : incoming;
        });
      }),
    [],
  );

  function confirmNow(): void {
    if (!alarm) return;
    void arm(); // the tap is a gesture: keep the audio armed for the next mark
    stopLoop();
    const upTo = ringing[ringing.length - 1];
    if (!upTo) return;
    setAck(storeAck({ shiftId: alarm.shiftId, upTo, sent: false }));
    broadcast({ type: 'alarm-confirmed', shiftId: alarm.shiftId, upTo });
    send(alarm.shiftId, upTo, 0);
  }

  function retest(): void {
    if (!alarm) return;
    void playTest(alarm.sound).then((ok) => setSoundBlocked(!ok));
  }

  function deadlineOf(key: string): string {
    const open = alarm?.openSlots.find((s) => s.scheduledAt === key);
    return open ? open.deadlineAt : new Date(Date.parse(key) + (alarm?.confirmMinutes ?? 0) * MS_PER_MINUTE).toISOString();
  }

  if (!alarm) return null;
  const newest = ringing[ringing.length - 1];
  const deadline = newest ? deadlineOf(newest) : null;
  return (
    <>
      {newest && deadline && (
        <AlarmOverlay markLabel={clockIn(newest, tz)} deadlineLabel={clockIn(deadline, tz)} overdue={serverNow > Date.parse(deadline)} onConfirm={confirmNow} />
      )}
      <div className="pointer-events-none fixed inset-x-0 bottom-16 z-40 flex flex-col items-center gap-2 px-4 sm:bottom-4 lg:pl-56">
        {retrying && (
          <p role="status" className="pointer-events-auto rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground">
            {RETRY_BANNER}
          </p>
        )}
        {blocked ? (
          <button type="button" className="pointer-events-auto rounded-lg bg-destructive px-3 py-2 text-sm text-destructive-foreground" onClick={retest}>
            {BLOCKED_BANNER}
          </button>
        ) : (
          !armed && (
            <button type="button" className="pointer-events-auto rounded-lg bg-warning px-3 py-2 text-sm text-warning-fg" onClick={() => void arm()}>
              {ARM_BANNER}
            </button>
          )
        )}
      </div>
    </>
  );
}
